import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import MovieCard from '@/components/base/MovieCard';
import { movies } from '@/mocks/movies';
import { categories } from '@/mocks/categories';

const heroMovies = movies.filter((m) => m.isHot).slice(0, 5);
const hotMovies = movies.filter((m) => m.isHot);
const newMovies = movies.filter((m) => m.isNew);
const topRatedMovies = [...movies].sort((a, b) => b.rating - a.rating).slice(0, 8);

export default function Home() {
  const [currentSlide, setCurrentSlide] = useState(0);

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % heroMovies.length);
  }, []);

  const prevSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + heroMovies.length) % heroMovies.length);
  }, []);

  useEffect(() => {
    const timer = setInterval(nextSlide, 5000);
    return () => clearInterval(timer);
  }, [nextSlide]);

  return (
    <>
      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          {/* Hero Section */}
          <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden">
            {heroMovies.map((movie, index) => (
              <div
                key={movie.id}
                className={`absolute inset-0 transition-opacity duration-700 ${
                  index === currentSlide ? 'opacity-100' : 'opacity-0'
                }`}
              >
                <img
                  src={movie.heroUrl}
                  alt={movie.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-background-50"></div>
              </div>
            ))}

            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-full max-w-5xl mx-auto px-4 md:px-6 text-center">
                <div className="animate-fade-in" key={currentSlide}>
                  <h1 className="text-3xl md:text-5xl font-bold text-white mb-3 font-heading">
                    {heroMovies[currentSlide].title}
                  </h1>
                  <p className="text-sm md:text-base text-white/70 max-w-2xl mx-auto mb-5 line-clamp-2">
                    {heroMovies[currentSlide].description}
                  </p>
                  <div className="flex items-center justify-center gap-3 mb-6">
                    <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-accent-500/90 text-background-950 text-sm font-bold">
                      <i className="ri-star-fill"></i> {heroMovies[currentSlide].rating} 分
                    </span>
                    <span className="text-white/60 text-sm">{heroMovies[currentSlide].year}</span>
                    <span className="text-white/60 text-sm">{heroMovies[currentSlide].duration}</span>
                  </div>
                  <Link
                    to={`/article/${heroMovies[currentSlide].id}`}
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 hover:bg-primary-600 text-background-50 font-semibold text-sm transition-all duration-200 cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-play-fill text-lg"></i> 立即观看
                  </Link>
                </div>
              </div>
            </div>

            <button
              onClick={prevSlide}
              className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-all cursor-pointer"
            >
              <i className="ri-arrow-left-s-line text-lg"></i>
            </button>
            <button
              onClick={nextSlide}
              className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-all cursor-pointer"
            >
              <i className="ri-arrow-right-s-line text-lg"></i>
            </button>

            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2">
              {heroMovies.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentSlide(index)}
                  className={`w-2 h-2 rounded-full transition-all cursor-pointer ${
                    index === currentSlide ? 'bg-primary-500 w-6' : 'bg-white/40 hover:bg-white/60'
                  }`}
                />
              ))}
            </div>
          </section>

          {/* Category Navigation */}
          <section className="px-4 md:px-6 lg:px-8 py-8 md:py-10">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center justify-center gap-1 md:gap-2 flex-wrap">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={cat.slug === 'all' ? '/' : `/category/${cat.slug}`}
                    className="flex items-center gap-1.5 px-3 py-2 md:px-4 md:py-2.5 rounded-full bg-background-100 hover:bg-background-200 text-foreground-300 hover:text-foreground-50 text-xs md:text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap"
                  >
                    <i className={`${cat.icon} text-sm md:text-base`}></i>
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>
          </section>

          {/* Hot Movies */}
          <section className="px-4 md:px-6 lg:px-8 py-8 md:py-10">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl md:text-2xl font-bold text-foreground-50 font-heading">
                  <i className="ri-fire-line text-primary-500 mr-2"></i>
                  热门推荐
                </h2>
                <Link
                  to="/category/all"
                  className="text-sm text-foreground-400 hover:text-primary-500 transition-colors flex items-center gap-1 whitespace-nowrap"
                >
                  查看全部 <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 md:gap-4">
                {hotMovies.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>
            </div>
          </section>

          {/* Featured Banner */}
          <section className="px-4 md:px-6 lg:px-8 py-8 md:py-10">
            <div className="max-w-7xl mx-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {heroMovies.slice(0, 2).map((movie) => (
                  <MovieCard key={movie.id} movie={movie} variant="featured" />
                ))}
              </div>
            </div>
          </section>

          {/* New Releases */}
          <section className="px-4 md:px-6 lg:px-8 py-8 md:py-10">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl md:text-2xl font-bold text-foreground-50 font-heading">
                  <i className="ri-sparkling-line text-accent-500 mr-2"></i>
                  最新上映
                </h2>
                <Link
                  to="/category/all"
                  className="text-sm text-foreground-400 hover:text-primary-500 transition-colors flex items-center gap-1 whitespace-nowrap"
                >
                  查看全部 <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 md:gap-4">
                {newMovies.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>
            </div>
          </section>

          {/* Top Rated */}
          <section className="px-4 md:px-6 lg:px-8 py-8 md:py-10">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl md:text-2xl font-bold text-foreground-50 font-heading">
                  <i className="ri-trophy-line text-accent-500 mr-2"></i>
                  高分必看
                </h2>
                <Link
                  to="/category/all"
                  className="text-sm text-foreground-400 hover:text-primary-500 transition-colors flex items-center gap-1 whitespace-nowrap"
                >
                  查看全部 <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
                {topRatedMovies.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} variant="compact" />
                ))}
              </div>
            </div>
          </section>

          {/* Stats Section */}
          <section className="px-4 md:px-6 lg:px-8 py-12 md:py-16 bg-background-100">
            <div className="max-w-5xl mx-auto">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
                <div className="animate-fade-in">
                  <div className="text-3xl md:text-4xl font-bold text-accent-500 font-heading">20,000+</div>
                  <p className="text-sm text-foreground-400 mt-2">高清电影资源</p>
                </div>
                <div className="animate-fade-in">
                  <div className="text-3xl md:text-4xl font-bold text-primary-500 font-heading">99.9%</div>
                  <p className="text-sm text-foreground-400 mt-2">播放成功率</p>
                </div>
                <div className="animate-fade-in">
                  <div className="text-3xl md:text-4xl font-bold text-accent-500 font-heading">500万+</div>
                  <p className="text-sm text-foreground-400 mt-2">日活跃用户</p>
                </div>
                <div className="animate-fade-in">
                  <div className="text-3xl md:text-4xl font-bold text-primary-500 font-heading">0</div>
                  <p className="text-sm text-foreground-400 mt-2">广告干扰</p>
                </div>
              </div>
            </div>
          </section>

          {/* Why Us */}
          <section className="px-4 md:px-6 lg:px-8 py-12 md:py-16">
            <div className="max-w-5xl mx-auto text-center">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-50 font-heading mb-4">
                为什么选择<strong className="text-primary-500">6080电影网</strong>？
              </h2>
              <p className="text-foreground-400 text-sm md:text-base max-w-2xl mx-auto mb-10">
                我们致力于提供最优质的免费高清电影在线观看体验，让每一位影迷都能享受极速、无广告的观影乐趣。
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                <div className="p-6 rounded-xl bg-background-100 hover:bg-background-200 transition-colors">
                  <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-primary-500/20 mb-4 mx-auto">
                    <i className="ri-speed-up-line text-primary-500 text-2xl"></i>
                  </div>
                  <h3 className="text-base font-bold text-foreground-50 mb-2">极速秒开</h3>
                  <p className="text-sm text-foreground-400">采用全球CDN加速技术，高清电影即点即看，告别缓冲等待</p>
                </div>
                <div className="p-6 rounded-xl bg-background-100 hover:bg-background-200 transition-colors">
                  <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-accent-500/20 mb-4 mx-auto">
                    <i className="ri-shield-check-line text-accent-500 text-2xl"></i>
                  </div>
                  <h3 className="text-base font-bold text-foreground-50 mb-2">纯净无广告</h3>
                  <p className="text-sm text-foreground-400">观影全程无广告干扰，沉浸式享受每一部好电影</p>
                </div>
                <div className="p-6 rounded-xl bg-background-100 hover:bg-background-200 transition-colors">
                  <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-secondary-500/20 mb-4 mx-auto">
                    <i className="ri-movie-2-line text-secondary-500 text-2xl"></i>
                  </div>
                  <h3 className="text-base font-bold text-foreground-50 mb-2">海量资源</h3>
                  <p className="text-sm text-foreground-400">超过20000部高清电影免费看，每日更新最新大片</p>
                </div>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}